import { Screen } from '@/shared/components/ios-screen';
import React, { FC, useEffect, useId } from 'react';
import { UseHttpMutationOptions, useHttpMutation } from '@/api';
import { AxiosRequestConfig } from 'axios';
import {
  NotEnoughCoins,
  useAlert,
  useNotEnoughCoins,
} from '@/shared/components';
import { Texts } from '@/shared/types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserState } from '@/redux/reducers';
import { BhabiStorage } from '@/shared/lib';
import { useNavigate } from 'react-router-dom';
import { IN_PLAY_ROUTE } from '../in-play';
import { API_URLS } from '../consts';
import { Store } from '@/redux';
import { GAME_RULES_ROUTES } from '../../unauthenticated';
import { Minus, Plus } from 'lucide-react';
import { MAX_PLAYERS, MIN_PLAYERS, clampPlayers } from './seats';
import { Link } from 'react-router-dom';
import { INVITE_PLAYER_ROUTE } from '../invite-players';

export declare type CreateRoomInput = {
  NumberOfPlayers: number;
};

export const useCreateRoom = (
  options?: UseHttpMutationOptions,
  config?: AxiosRequestConfig
) =>
  useHttpMutation<{ token: string }, CreateRoomInput>(
    API_URLS.CREATE_ROOM,
    'POST',
    options,
    config
  );

export interface CreateRoomPageProps {
  texts?: Texts<'createRoomButton' | 'message' | 'createRoom'>;
}

export const CreateRoomPage: FC<CreateRoomPageProps> = ({
  texts = {
    createRoomButton: 'Create table & invite',
    message: 'Minimum 2 players and maximum 9 players',
    createRoom: 'Deal a new table',
  },
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<CreateRoomInput>({
    mode: 'onChange',
    defaultValues: {
      NumberOfPlayers: 2,
    },
  });

  const playerCount = Number(watch('NumberOfPlayers'));
  const validCount =
    Number.isFinite(playerCount) &&
    playerCount >= MIN_PLAYERS &&
    playerCount <= MAX_PLAYERS;

  const { mutate: createRoom, isLoading } = useCreateRoom();
  const { addAlert } = useAlert();
  const dispatch = useDispatch();
  const { isInRoom } = useSelector((state: Store) => state.userReducer);
  const nav = useNavigate();
  const inputId = useId();

  const {
    showCoinsMessage,
    checkIfEnoughCoins,
    hasEnoughCoins,
    setShowCoinsMessage,
  } = useNotEnoughCoins();

  useEffect(() => {
    if (isInRoom) {
      nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
    }
  }, [isInRoom, nav]);

  const step = (delta: number) => {
    const base = Number.isFinite(playerCount) ? playerCount : MIN_PLAYERS;
    setValue('NumberOfPlayers', clampPlayers(base + delta), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSubmit: SubmitHandler<CreateRoomInput> = data => {
    if (!hasEnoughCoins) {
      checkIfEnoughCoins();
      return;
    }

    createRoom(
      { NumberOfPlayers: Number(data.NumberOfPlayers) },
      {
        onError: (err: unknown) => {
          addAlert({
            message: err instanceof Error ? err.message : 'Error creating room',
            type: 'error',
          });
        },
        onSuccess: res => {
          // update token, this token will be used to join the room
          BhabiStorage.setToken(res.token);
          dispatch(
            updateUserState({
              isInRoom: true,
              token: res.token,
            })
          );
          nav(
            [
              INVITE_PLAYER_ROUTE.ABSOLUTE_PATH,
              '?tab=Users',
              '&selectMode=true',
              `&noOfPlayers=${data.NumberOfPlayers}`,
            ].join('')
          );
        },
      }
    );
  };

  const errorMessage =
    errors.NumberOfPlayers?.message ||
    (!validCount ? 'Choose between 2 and 9 players' : undefined);
  const busy = isLoading || isSubmitting;

  return (
    <Screen width="narrow" className="bh-cs">
      <NotEnoughCoins
        showCoinsMessage={showCoinsMessage}
        setShowCoinsMessage={setShowCoinsMessage}
      />

      <header className="bh-screen__head">
        <p className="bh-eyebrow">New table</p>
        <h1 className="bh-screen__title">{texts.createRoom}</h1>
        <p className="bh-screen__sub">
          Pick how many seats to deal, then invite friends or fill the empty
          seats with bots.{' '}
          <Link to={GAME_RULES_ROUTES.ABSOLUTE_PATH} className="bh-link">
            How to play
          </Link>
        </p>
      </header>

      <form
        className="st-card bh-cs__card bh-screen__fill"
        data-suit="clubs"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <label
          htmlFor={inputId}
          className="st-label"
          style={{ display: 'block', textAlign: 'center' }}
        >
          Players at the table
        </label>
        <div
          className="bh-stepper"
          style={{ ['--bh-text' as string]: '#0b1512' }}
        >
          <button
            type="button"
            className="st-btn st-btn--on-ivory"
            aria-label="Remove a seat"
            onClick={() => step(-1)}
            disabled={validCount && playerCount <= MIN_PLAYERS}
          >
            <Minus aria-hidden="true" />
          </button>
          <div className="bh-stepper__value">
            <input
              id={inputId}
              type="number"
              inputMode="numeric"
              min={MIN_PLAYERS}
              max={MAX_PLAYERS}
              aria-invalid={!!errorMessage}
              aria-describedby={`${inputId}-help`}
              {...register('NumberOfPlayers', {
                required: 'Number of players is required',
                min: {
                  value: MIN_PLAYERS,
                  message: 'Minimum 2 players required',
                },
                max: {
                  value: MAX_PLAYERS,
                  message: 'Maximum 9 players allowed',
                },
                valueAsNumber: true,
              })}
            />
            <small className="st-hint" style={{ display: 'block' }}>
              players
            </small>
          </div>
          <button
            type="button"
            className="st-btn st-btn--on-ivory"
            aria-label="Add a seat"
            onClick={() => step(1)}
            disabled={validCount && playerCount >= MAX_PLAYERS}
          >
            <Plus aria-hidden="true" />
          </button>
        </div>

        {validCount && (
          <ul className="bh-seats" aria-hidden="true">
            {Array.from({ length: playerCount }).map((_, i) => (
              <li key={i} />
            ))}
          </ul>
        )}

        <p
          id={`${inputId}-help`}
          className={errorMessage ? 'st-error' : 'st-hint'}
          role={errorMessage ? 'alert' : undefined}
          style={{ margin: 0, textAlign: 'center' }}
        >
          {errorMessage || texts.message}
        </p>

        <button
          type="submit"
          className="st-btn st-btn--primary st-btn--block"
          disabled={!validCount || busy}
          aria-busy={busy}
        >
          {busy ? 'Dealing your table…' : texts.createRoomButton}
        </button>
        <p className="st-hint" style={{ margin: 0, textAlign: 'center' }}>
          You&apos;ll pick who to invite next.
        </p>
      </form>
    </Screen>
  );
};
